import DataTable, { Column } from '@/components/CommonComponents/DataTable';
import SurveyPreviewModal from '@/components/CommonComponents/SurveyPreviewModal';
import ViewDetailsModal from '@/components/CommonComponents/ViewDetailsModal';
import { showToast } from '@/helper/toast.helper';
import { parseEmoji } from '@/helper/utils';
import { useExportFile } from '@/hooks/useExportFile';
import { usePermission } from '@/hooks/usePermission';
import { surveyManagementService } from '@/services/surveyManagementService/surveyManagementService';
import { surveySetManagementService } from '@/services/surveySetManagementService/surveySetManagementService';
import { AppDispatch, RootState } from '@/store';
import {
  deleteSurveyFromSet,
  getSurveySetById,
  updateSurveyStatusInSet,
} from '@/store/slices/surveySetManagementSlice/surveySetManagementSlice';
import { SurveyListItem, SurveyQuestion } from '@/types/survey.types';
import { RowPermissions } from '@/types/surveySet.types';
import { Info } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ClientSearchFilter from '../CommonComponents/ClientSearchFilter';
import DeleteModal from '../CommonComponents/DeleteModal';
import Pagination from '../CommonComponents/Pagination';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';

type Survey = {
  id: string;
  title: string;
  questions: number;
  status: boolean;
  created: string;
  start: string;
  end: string;
  questionsJson?: SurveyQuestion[];
  permissions?: RowPermissions;
};

const SelectedSurveySet: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { exportFile } = useExportFile();

  const { selected: surveySetDetails, loading } = useSelector(
    (state: RootState) => state.surveySet,
  );

  const [allSurveys, setAllSurveys] = useState<Survey[]>([]);
  const [filteredSurveys, setFilteredSurveys] = useState<Survey[]>([]);

  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSurveySetModalOpen, setIsSurveySetModalOpen] = useState(false);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewSurvey, setPreviewSurvey] = useState<SurveyListItem | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState<Survey | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const surveySetId = location.state?.surveySetId;

  const permissions = {
    create: usePermission('write', 'surveys'),
    edit: usePermission('edit', 'surveys'),
    export: usePermission('export', 'surveys'),
    delete: usePermission('delete', 'surveys'),
  };

  useEffect(() => {
    if (surveySetId) {
      dispatch(getSurveySetById(surveySetId));
    }
  }, [dispatch, surveySetId]);

  useEffect(() => {
    if (surveySetDetails?.surveys && surveySetDetails.surveys.length > 0) {
      const baseDate = new Date(surveySetDetails.startDate!);
      const mappedSurveys = surveySetDetails.surveys.map((item: any, index: number) => {
        const surveyDate = new Date(baseDate);
        surveyDate.setDate(baseDate.getDate() + index);

        const formattedDate = surveyDate.toLocaleDateString('en-CA');

        return {
          id: item.survey.id.toString(),
          title: item.survey.title,
          questions: item.survey.totalQuestions,
          defaultLanguageId: item.survey.defaultLanguageId,
          odkFileUrl: item.survey.odkFileUrl,
          languages: item.survey.languages,
          status: item.status,
          created: new Date(item.survey.createdAt).toLocaleDateString('en-CA'),
          start: formattedDate ?? 'N/A',
          end: surveySetDetails?.endDate?.split('T')[0] ?? 'N/A',
          questionsJson: item.survey.questionsJson || [],
          _permissions: permissions,
        };
      });
      setAllSurveys(mappedSurveys);
      setFilteredSurveys(mappedSurveys);
    } else {
      setAllSurveys([]);
      setFilteredSurveys([]);
    }
  }, [surveySetDetails]);

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = filteredSurveys.slice(startIndex, endIndex);
  const totalRecords = filteredSurveys.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleViewSurvey = (survey: Survey) => {
    setSelectedSurvey(survey);
    setIsModalOpen(true);
  };

  const openDeleteModal = (survey: Survey) => {
    // if (survey.status === true) {
    //   showToast.error('Cannot delete an active survey');
    //   return;
    // }

    setSurveyToDelete(survey);
    setIsDeleteModalOpen(true);
  };

  const handleEditSurvey = (row: any) => {
    navigate('/dashboard/surveyset-management/survey/edit', {
      state: { survey: row },
    });
  };

  const handleDelete = async () => {
    if (!surveyToDelete) return;

    setFilteredSurveys((prev) => prev.filter((s) => s.id !== surveyToDelete.id));
    setAllSurveys((prev) => prev.filter((s) => s.id !== surveyToDelete.id));

    setIsDeleteModalOpen(false);
    const tempSurvey = surveyToDelete;
    setSurveyToDelete(null);

    try {
      await dispatch(
        deleteSurveyFromSet({
          surveySetId: surveySetId,
          surveyId: tempSurvey.id,
        }),
      ).unwrap();

      showToast.success('Survey removed from survey set successfully');
    } catch (error: any) {
      showToast.error(error || 'Failed to remove survey from survey set');
      setFilteredSurveys((prev) => [...prev, tempSurvey]);
      setAllSurveys((prev) => [...prev, tempSurvey]);
    }
  };

  const handleStatusToggle = async (id: string, newStatus: boolean) => {
    const updateSurveyStatus = (surveys: Survey[]) =>
      surveys.map((s) => (s.id === id ? { ...s, status: newStatus } : s));

    setFilteredSurveys(updateSurveyStatus);
    setAllSurveys(updateSurveyStatus);

    try {
      await dispatch(
        updateSurveyStatusInSet({ surveySetId, surveyId: id, status: newStatus }),
      ).unwrap();
    } catch (error: any) {
      showToast.error(error?.message || 'Failed to update survey status');

      const revertStatus = (surveys: Survey[]) =>
        surveys.map((s) => (s.id === id ? { ...s, status: !newStatus } : s));

      setFilteredSurveys(revertStatus);
      setAllSurveys(revertStatus);
    }
  };

  const handleExportSurveyResponse = (row?: Survey) => {
    exportFile(
      () =>
        surveySetManagementService.exportSurveyResponse({
          surveySetId: surveySetDetails?.id,
          surveyId: row?.id ? Number(row?.id) : null,
        }),
      `survey_${row ? row?.id : ''}_${Date.now()}.csv`,
    );
  };

  const handlePreview = async (survey: Survey) => {
    try {
      const surveyData = await surveyManagementService.getSurveyById(survey.id);

      // Find defaultLanguageId from defaultLanguage string
      let defaultLanguageId = surveyData.data?.defaultLanguageId;
      if (!defaultLanguageId && surveyData.data?.defaultLanguage && surveyData.data?.languages) {
        const defaultLang = surveyData.data.languages.find(
          (lang: any) => `${lang.name} (${lang.code})` === surveyData.data.defaultLanguage,
        );
        defaultLanguageId = defaultLang?.id;
      }

      const surveyListItem: SurveyListItem = {
        id: parseInt(survey.id),
        title: survey.title,
        status: survey.status,
        createdAt: survey.created,
        questionsJson: survey.questionsJson || [],
        defaultLanguageId: defaultLanguageId,
        languages: surveyData.data?.languages || [],
        odkFileUrl: surveyData.data?.odkFileUrl || '',
        surveySets: surveyData.data?.surveySets || [],
        createdBy: surveyData.data?.createdBy || null,
      };
      setPreviewSurvey(surveyListItem);
      setIsPreviewOpen(true);
    } catch (error) {
      showToast.error('Failed to load survey details');
    }
  };

  const surveyColumns: Column<Survey>[] = [
    { key: 'id', header: 'Survey ID' },
    { key: 'title', header: 'Survey Title' },
    { key: 'questions', header: 'Questions' },
    { key: 'status', header: 'Status' },
    { key: 'created', header: 'Created On' },
    { key: 'start', header: 'Start Date' },
    { key: 'end', header: 'End Date' },
  ];

  return (
    <PageHeaderWrapper
      title="Survey-Set Management"
      description="Manage and monitor surveys across all projects"
      onAdd={() => navigate('/dashboard/surveyset-management')}
      addLabel="Return to Survey Set List"
      onExport={handleExportSurveyResponse}
      exportLabel="Export"
      showActions
      permissions={permissions}
    >
      {/* Survey Set Title */}
      {surveySetDetails && (
        <div className="bg-[#F2EBEE] rounded-xl p-3">
          <div className="flex justify-between items-stretch">
            {/* LEFT SECTION */}
            <div className="flex items-stretch gap-4">
              <div className="bg-[#AFAFAF]/50 rounded-lg flex items-center justify-center p-3 aspect-square">
                <span className="text-3xl">{parseEmoji('')}</span>
              </div>

              <div className="flex flex-col justify-between capitalize">
                <div className="text-lg font-medium">{surveySetDetails?.id}</div>
                <div className="text-2xl font-semibold">{surveySetDetails?.title}</div>
              </div>
            </div>
            {/* RIGHT SECTION */}
            <div className="flex gap-2">
              <button
                onClick={() => setIsSurveySetModalOpen(true)}
                className="rounded-lg px-3 bg-[#AFAFAF33] hover:bg-[#9c9b9b33] disabled:opacity-40"
              >
                <Info size={35} strokeWidth={1.5} color="#4A082D" />
              </button>
              <button
                onClick={() => navigate(-1)}
                className="rounded-lg px-3 bg-[#AFAFAF33] hover:bg-[#9c9b9b33] disabled:opacity-40"
              >
                <img src="/Vector.png" alt="Expand" className="w-8 h-8" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search + Filter */}
      <ClientSearchFilter<Survey>
        fields={[
          {
            type: 'search',
            key: 'search',
            placeholder: 'Search Surveys...',
          },

          {
            type: 'radio-select',
            key: 'status',
            label: 'select',
            options: ['Active', 'Inactive'],
          },
        ]}
        data={allSurveys}
        onFilter={setFilteredSurveys}
        searchableKeys={['id', 'title']}
        customFilterFn={(item, filters) => {
          // Search filter
          if (filters.search && filters.search.trim() !== '') {
            const searchLower = filters.search.toLowerCase();
            const matchesSearch =
              item.id.toLowerCase().includes(searchLower) ||
              item.title.toLowerCase().includes(searchLower);
            if (!matchesSearch) return false;
          }

          // Status filter
          if (filters.status && !filters.status.startsWith('Select')) {
            const statusBool = filters.status === 'Active';
            if (item.status !== statusBool) return false;
          }

          return true;
        }}
      />
      <div>
        {/* Table */}
        <DataTable
          listTitle="Surveys in Survey Set"
          columns={surveyColumns}
          data={currentData}
          loading={loading}
          onView={handleViewSurvey}
          onEdit={(row) => {
            handleEditSurvey(row);
          }}
          onPreview={handlePreview}
          onDelete={openDeleteModal}
          onStatusToggle={handleStatusToggle}
          onExport={(row) => handleExportSurveyResponse(row)}
          permissions={permissions}
        />

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          onPageChange={handlePageChange}
        />
      </div>

      {/* View Details Modal */}
      {isModalOpen && selectedSurvey && (
        <ViewDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Survey Details"
          data={selectedSurvey}
          fields={[
            { label: 'Survey ID', value: selectedSurvey.id },
            { label: 'Survey Title', value: selectedSurvey.title },
            {
              label: 'Total Questions',
              value: String(selectedSurvey.questions),
            },

            { label: 'Created On', value: selectedSurvey.created },
            { label: 'Start Date', value: selectedSurvey.start },
            { label: 'End Date', value: selectedSurvey.end },
            {
              label: 'Status',
              value: selectedSurvey.status ? 'Active' : 'Inactive',
              list: true,
              fullWidth: true,
            },
          ]}
        />
      )}

      {isSurveySetModalOpen && surveySetDetails && (
        <ViewDetailsModal
          isOpen={isSurveySetModalOpen}
          onClose={() => setIsSurveySetModalOpen(false)}
          title="Survey-Set Details"
          data={surveySetDetails}
          fields={[
            { label: 'Survey-Set ID', value: surveySetDetails.id },

            { label: 'Survey-Set Title', value: surveySetDetails.title },

            { label: 'How Many Surveys?', value: surveySetDetails.numberOfSurveys },

            { label: 'Linked Project', value: surveySetDetails.site?.project?.name },

            { label: 'Linked Site', value: surveySetDetails.site?.name },

            { label: 'Poster Code', value: surveySetDetails.posterCode || 'N/A' },

            { label: 'Currency Type', value: surveySetDetails.currency },

            { label: 'Reward Amount', value: `₹${surveySetDetails.rewardAmount}` },

            { label: 'Claim Reward Type', value: surveySetDetails.claimType },

            {
              label: 'Participants Limit',
              value:
                surveySetDetails.minParticipants && surveySetDetails.maxParticipants
                  ? `Min-${surveySetDetails.minParticipants}, Max-${surveySetDetails.maxParticipants}`
                  : 'N/A',
            },

            {
              label: 'Status',
              value: surveySetDetails.status ? 'Active' : 'Inactive',
              list: true,
            },

            {
              label: 'Start Date',
              value: surveySetDetails.startDate
                ? new Date(surveySetDetails.startDate).toLocaleDateString('en-CA')
                : 'N/A',
            },

            {
              label: 'End Date',
              value: surveySetDetails.endDate
                ? new Date(surveySetDetails.endDate).toLocaleDateString('en-CA')
                : 'N/A',
            },

            { label: 'Submission Role', value: surveySetDetails.submissionRule },

            {
              label: 'Survey List',
              value: surveySetDetails.surveys?.map((x, idx) => `${x.survey.title}`) || ['N/A'],
              list: true,
              fullWidth: true,
            },
          ]}
        />
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSurveyToDelete(null);
        }}
        onDelete={handleDelete}
        pageTitle="Survey"
        itemName={surveyToDelete?.title}
        fromItem={surveySetDetails?.title}
      />

      {/* Survey Preview Modal */}
      <SurveyPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        survey={previewSurvey}
      />
    </PageHeaderWrapper>
  );
};

export default SelectedSurveySet;
